import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { getExactImageProps } from '../utils/imageOptimizer';
import { getGroupCardStyles, useIsDarkMode } from '../utils/colorGenerator';
import {
    TeamIcon as Groups,
    CalendarIcon as Calendar,
    LocationIcon as Location,
    ClockIcon as Clock,
    ArrowRightIcon as ArrowRight
} from './Icons';
import GroupInquiryWizard from './GroupInquiryWizard';

const DEFAULT_HEADER = {
    title: 'Unsere Gruppen & Stufen',
    subtitle: 'Entdecke die passende Stufe für dein Alter – von den Jüngsten bis zu den Erwachsenen ist für jeden das passende Abenteuer dabei.'
};

const weekDays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export const getGroupAgeInfo = (groupOrData) => {
    if (!groupOrData) return null;
    const currentYear = new Date().getFullYear();
    const stufe = (groupOrData.altersstufe || '').trim();

    if (stufe.toLowerCase() === 'meute' || stufe.toLowerCase() === 'wölflinge') {
        const start = currentYear - 10;
        const end = currentYear - 6;
        return {
            isMeute: true,
            ageLabel: '6–10 Jahre',
            birthYearsLabel: `Jahrgänge ${start}–${end}`,
            badge: `6–10 Jahre (${start}–${end})`
        };
    }

    const yStart = groupOrData.birthYearStart ? parseInt(groupOrData.birthYearStart) : null;
    const yEnd = groupOrData.birthYearEnd ? parseInt(groupOrData.birthYearEnd) : null;

    if (yStart && yEnd) {
        const minYear = Math.min(yStart, yEnd);
        const maxYear = Math.max(yStart, yEnd);
        const minAge = currentYear - maxYear;
        const maxAge = currentYear - minYear;
        return {
            isMeute: false,
            ageLabel: `ca. ${minAge}–${maxAge} Jahre`,
            birthYearsLabel: `Jahrgänge ${minYear}–${maxYear}`,
            badge: `ca. ${minAge}–${maxAge} Jahre (${minYear}–${maxYear})`
        };
    } else if (yStart) {
        const age = currentYear - yStart;
        return {
            isMeute: false,
            ageLabel: `ca. ${age} Jahre`,
            birthYearsLabel: `Jahrgang ${yStart}`,
            badge: `ca. ${age} Jahre (${yStart})`
        };
    }

    return null;
};

const GroupsSection = ({ header = DEFAULT_HEADER, modules = {} }) => {
    const navigate = useNavigate();
    const { getAssetUrl } = useContext(AuthContext);
    const isDarkMode = useIsDarkMode();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showInquiryWizard, setShowInquiryWizard] = useState(false);

    const isInquiryEnabled = modules?.groupInquiries === true;

    const handleSchnuppern = (group, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        navigate(`/neuanmeldung?group=${encodeURIComponent(group.name)}`);
    };

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const apiUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
                const { data } = await axios.get(`${apiUrl}/groups`);
                setGroups(Array.isArray(data) ? data.filter(g => g.isActive !== false && g.isArchived !== true) : []);
            } catch (err) {
                console.error('Failed to load groups for GroupsSection', err);
            } finally {
                setLoading(false);
            }
        };
        fetchGroups();
    }, []);

    const altersstufen = ['all', ...new Set(groups.map(g => g.altersstufe).filter(Boolean))];

    const filteredGroups = selectedFilter === 'all'
        ? groups
        : groups.filter(g => g.altersstufe === selectedFilter);

    if (loading || !groups || groups.length === 0) {
        return null;
    }

    return (
        <section id="groups-section" className="section-reveal delay-220 scroll-mt-28 bg-surface">
            <div className="container mx-auto px-4 py-16 max-w-7xl relative">
            {/* SVG Filter for Glow-in-the-Dark Easter Egg (#14FF00) */}
            <svg width="0" height="0" className="absolute w-0 h-0 pointer-events-none opacity-0 overflow-hidden" aria-hidden="true">
                <defs>
                    <filter id="badge-glow-green" colorInterpolationFilters="sRGB" x="-20%" y="-20%" width="140%" height="140%">
                        <feColorMatrix
                            type="matrix"
                            in="SourceGraphic"
                            result="luminance"
                            values="
                                0.3333 0.3333 0.3333 0 0
                                0.3333 0.3333 0.3333 0 0
                                0.3333 0.3333 0.3333 0 0
                                0      0      0      1 0
                            "
                        />
                        <feComponentTransfer in="luminance" result="whiteMask">
                            <feFuncR type="linear" slope="3.5" intercept="-2.3" />
                            <feFuncG type="linear" slope="3.5" intercept="-2.3" />
                            <feFuncB type="linear" slope="3.5" intercept="-2.3" />
                        </feComponentTransfer>
                        <feColorMatrix
                            type="matrix"
                            in="whiteMask"
                            result="greenGlow"
                            values="
                                0.0784 0 0 0 0
                                1.0000 0 0 0 0
                                0.0000 0 0 0 0
                                0      0 0 1 0
                            "
                        />
                        <feComposite in="SourceGraphic" in2="whiteMask" operator="arithmetic" k1="0" k2="1" k3="-1" k4="0" result="nonWhite" />
                        <feComposite in="nonWhite" in2="greenGlow" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="colorized" />
                        <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor="#14FF00" floodOpacity="0.8" />
                    </filter>
                </defs>
            </svg>

            {/* Injected Glow Styles */}
            <style>{`
                .dark .badge-glow-in-dark {
                    box-shadow: 0 0 16px rgba(20, 255, 0, 0.55), 0 0 32px rgba(20, 255, 0, 0.25) !important;
                    border-color: #14FF00 !important;
                    animation: phosphorPulse 3s ease-in-out infinite alternate;
                }
                .dark .badge-glow-in-dark img {
                    filter: url(#badge-glow-green) !important;
                    transition: filter 0.4s ease;
                }
                .dark .badge-glow-in-dark .fallback-letter {
                    color: #14FF00 !important;
                    text-shadow: 0 0 10px #14FF00, 0 0 20px rgba(20, 255, 0, 0.9) !important;
                }
                @keyframes phosphorPulse {
                    0% {
                        box-shadow: 0 0 12px rgba(20, 255, 0, 0.45), 0 0 24px rgba(20, 255, 0, 0.2);
                    }
                    100% {
                        box-shadow: 0 0 20px rgba(20, 255, 0, 0.75), 0 0 40px rgba(20, 255, 0, 0.35);
                    }
                }
            `}</style>

            {/* Section Header */}
            <div className="text-center max-w-3xl mx-auto mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-container text-primary-onContainer text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
                    <Groups style={{ fontSize: 18 }} />
                    <span>Gemeinschaft & Stufen</span>
                </div>
                <h2 className="text-display-small md:text-headline-large font-bold text-surface-on mb-4">
                    {header?.title || DEFAULT_HEADER.title}
                </h2>
                <p className="text-body-large text-surface-onVariant leading-relaxed">
                    {header?.subtitle || DEFAULT_HEADER.subtitle}
                </p>

                {isInquiryEnabled && (
                    <div className="mt-6 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setShowInquiryWizard(true)}
                            className="md3-btn-filled px-6 py-3 rounded-full flex items-center gap-2 font-bold text-sm shadow-elevation-2 transition-transform hover:scale-105"
                        >
                            <Groups style={{ fontSize: 18 }} />
                            <span>Passende Gruppe finden oder für Warteliste eintragen</span>
                        </button>
                    </div>
                )}

                {/* Filter Tabs if multiple stufen */}
                {altersstufen.length > 2 && (
                    <div className="flex flex-wrap justify-center gap-2 mt-8">
                        {altersstufen.map(stufe => (
                            <button
                                key={stufe}
                                onClick={() => setSelectedFilter(stufe)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                                    selectedFilter === stufe
                                        ? 'bg-primary text-primary-on shadow-md scale-105'
                                        : 'bg-surface-container text-surface-on hover:bg-surface-containerHigh'
                                }`}
                            >
                                {stufe === 'all' ? 'Alle Stufen' : stufe}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Groups Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredGroups.map(group => {
                    const groupStyles = getGroupCardStyles(group.color || '#009640', isDarkMode);
                    const hasSchedule = Boolean(
                        group.meetingTime?.trim() ||
                        (group.standardSchedule && (
                            (group.standardSchedule.dayOfWeek !== undefined && group.standardSchedule.dayOfWeek !== null && group.standardSchedule.dayOfWeek !== -1 && group.standardSchedule.dayOfWeek !== '') ||
                            group.standardSchedule.startTime?.trim()
                        ))
                    );
                    const ageInfo = getGroupAgeInfo(group);
                    const targetLink = `/gruppen/${group.slug || group._id}`;

                    return (
                        <div
                            key={group._id}
                            className="md3-card-elevated rounded-3xl p-6 sm:p-8 flex flex-col justify-between hover:shadow-xl transition-all duration-300 border border-outlineVariant/30 relative overflow-hidden group/card hover:-translate-y-1 text-center"
                        >
                            {/* Accent Top Bar */}
                            <div
                                className="absolute top-0 left-0 right-0 h-2.5 transition-all group-hover/card:h-3.5"
                                style={groupStyles.topBar}
                            />

                            <div className="flex flex-col items-center">
                                {/* Centered Large Logo / Wappen */}
                                <Link
                                    to={targetLink}
                                    className="block mb-4 pt-2 group-hover/card:scale-105 transition-transform duration-300"
                                >
                                    {group.logoUrl || group.imageUrl ? (
                                        <div
                                            className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden shadow-md border-3 shrink-0 bg-surface-container flex items-center justify-center relative transition-all duration-300 ${
                                                group.glowInTheDark ? 'badge-glow-in-dark border-outlineVariant/40' : 'border-outlineVariant/40'
                                            }`}
                                            title={group.glowInTheDark ? "Nachtleuchtendes Abzeichen (Leuchtet im Dunkeln grün)" : group.name}
                                        >
                                            <img
                                                {...getExactImageProps(
                                                    getAssetUrl,
                                                    group.logoUrl || group.imageUrl,
                                                    112,
                                                    {
                                                        quality: 85,
                                                        scales: [1, 2, 3],
                                                        alt: group.name || 'Gruppenabzeichen'
                                                    }
                                                )}
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center font-black text-3xl sm:text-4xl shadow-md shrink-0 transition-all duration-300 ${
                                                group.glowInTheDark ? 'badge-glow-in-dark' : ''
                                            }`}
                                            style={groupStyles.avatar}
                                            title={group.glowInTheDark ? "Nachtleuchtendes Abzeichen (Leuchtet im Dunkeln grün)" : group.name}
                                        >
                                            <span className={group.glowInTheDark ? 'fallback-letter' : ''}>
                                                {group.name.charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                </Link>

                                {/* Badges */}
                                <div className="flex items-center justify-center gap-1.5 flex-wrap mb-2">
                                    <span
                                        className="inline-block px-3 py-0.5 rounded-full text-xs font-bold transition-colors"
                                        style={groupStyles.badge}
                                    >
                                        {group.altersstufe || 'Gruppe'}
                                    </span>
                                    {ageInfo && (
                                        <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-surface-containerHigh text-surface-on">
                                            {ageInfo.badge}
                                        </span>
                                    )}
                                </div>

                                {/* Title */}
                                <Link to={targetLink}>
                                    <h3 className="text-headline-small font-bold text-surface-on hover:text-primary transition-colors mb-2">
                                        {group.name}
                                    </h3>
                                </Link>

                                {/* Motto Banner */}
                                {group.motto && (
                                    <div
                                        className="px-3.5 py-1.5 rounded-xl mb-3 text-xs font-semibold italic border transition-colors"
                                        style={groupStyles.motto}
                                    >
                                        „{group.motto}“
                                    </div>
                                )}

                                {/* Description */}
                                {group.description && (
                                    <p className="text-body-medium text-surface-onVariant mb-4 leading-relaxed line-clamp-3">
                                        {group.description}
                                    </p>
                                )}

                                {/* Compact Schedule Pill */}
                                {hasSchedule && (
                                    <div className="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 bg-surface-containerLow rounded-full mb-4 border border-outlineVariant/20 text-body-small text-surface-on">
                                        <Calendar style={{ fontSize: 15 }} className="text-primary shrink-0" />
                                        <span>
                                            {group.standardSchedule?.dayOfWeek !== undefined && group.standardSchedule.dayOfWeek !== null && group.standardSchedule.dayOfWeek !== -1
                                                ? `${weekDays[group.standardSchedule.dayOfWeek]}s`
                                                : group.meetingTime}
                                            {group.standardSchedule?.startTime ? ` (${group.standardSchedule.startTime} Uhr)` : ''}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* CTA Action Bar */}
                            <div className="pt-4 border-t border-outlineVariant/40 flex flex-col sm:flex-row items-center gap-2">
                                <button
                                    type="button"
                                    onClick={(e) => handleSchnuppern(group, e)}
                                    className="w-full flex-1 md3-btn-filled font-bold text-xs sm:text-sm py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
                                >
                                    <span>Direkt reinschnuppern</span>
                                </button>
                                <Link
                                    to={targetLink}
                                    className="w-full sm:w-auto px-3.5 py-2.5 rounded-xl border border-outlineVariant/50 bg-surface-container hover:bg-surface-containerHigh text-surface-on text-xs font-semibold flex items-center justify-center gap-1 transition-colors whitespace-nowrap"
                                    title="Gruppendetails ansehen"
                                >
                                    <span>Details</span>
                                    <ArrowRight style={{ fontSize: 15 }} />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isInquiryEnabled && (
                <GroupInquiryWizard
                    isOpen={showInquiryWizard}
                    onClose={() => setShowInquiryWizard(false)}
                />
            )}
        </div>
    </section>
    );
};

export default GroupsSection;

